import { useMemo } from "react";
import { WalletConfig } from "@/types/DocumentTemplate";
import type { Event } from "@/types/Event";
import { getPassType } from "./walletPassTypeSchemas";
import { applyEventTokenRules, type EventTokenParity } from "./passRenderRules";
import type { FieldGroup, RowCertainty } from "./passRenderRules";
import {
  buildWalletSamples,
  DeviceOnlyProvider,
  PassCertaintyProvider,
  WalletSamplesProvider,
} from "./preview/sharedPreviewBits";
import { EventTicketPreview } from "./preview/EventTicketPreview";
import { BoardingPassPreview } from "./preview/BoardingPassPreview";
import { CouponPreview } from "./preview/CouponPreview";
import { GenericPreview } from "./preview/GenericPreview";
import { StoreCardPreview } from "./preview/StoreCardPreview";
import { GooglePassPreview, type GoogleAiDisclosureMode } from "./preview/GooglePassPreview";

export type WalletPlatform = "apple" | "google";

type Props = {
  config: WalletConfig;
  currentLanguage: string;
  platform?: WalletPlatform;
  /**
   * The event this pass is for. Its name, date, time and location are what
   * `{{eventName}}` and friends resolve to on a real send, so handing it over
   * is the difference between a preview and a mock-up. Absent on the template
   * builder and the preset gallery, where a stand-in event stands in.
   */
  event?: Event | null;
  eventId?: string;
  /** Template primary language — the renderer's fallback per field map. */
  primaryLanguage?: string;
  /**
   * This config has ALREADY been through `applyPassRenderRules` for a real
   * guest, with the guest enriched by the event variables.
   *
   * Set it on those lanes and only those. The event-token pass below is not
   * idempotent — see the comment on `parity` — so running it a second time on a
   * substituted config deletes rows the guest lane kept.
   */
  preSubstituted?: boolean;
  /**
   * Draw ONLY what the device draws — no merge-field chips, no em-dash for a
   * missing value, no grey boxes naming empty image slots, no ghosted rows past
   * the device's field cap.
   *
   * ONE exception, and it is deliberate: the header-collision warning stays.
   * This docstring said otherwise and was wrong. The warning is about a LENGTH,
   * and the two views disagree about which length is on the card — the design
   * lane draws `{{companyName}}` where the device draws what it resolves to. It
   * was visible only in the lane whose text does not collide and hidden in the
   * lane that demonstrates the collision, so no view ever showed the warning
   * beside its own evidence. `sharedPreviewBits` carries the full argument, and
   * `deviceOnly.test.tsx` pins the keeping.
   *
   * The builder view (the default) and this one are both true and answer
   * different questions: "what am I building" versus "what does my guest get".
   * Nothing is deleted for it — the warnings an operator needs are exactly the
   * marks a pass does not carry, so they are suppressed here and kept there.
   *
   * Distinct from `preSubstituted`, which is about which RULES ran, not about
   * what is drawn.
   */
  deviceOnly?: boolean;
  /**
   * The rows `applyPassRenderRules` KEPT because the client could not decide
   * them for this guest, as it returns them: group -> field keys.
   *
   * Only the two guest lanes have this answer (the editor's guest walk and the
   * send dialog), and only they should pass it. Everything else leaves it off
   * and draws exactly what it draws today.
   *
   * It matters in the DEVICE view, where a row with no resolved value is
   * otherwise indistinguishable from a deliberate label-only spacer: without
   * this the card drew "TISCH" with a blank line under it while the caption
   * beneath said the server fills that row in. See `emptyValueMark`.
   *
   * Distinct from `preSubstituted` and `deviceOnly` both: those say which rules
   * ran and which marks are drawn; this says what the rules could not settle.
   */
  rows?: Partial<Record<FieldGroup, Record<string, RowCertainty>>>;
  /**
   * Art. 50 Abs. 2 KI-VO (plan D9): the pass's AI mode. Only the Google card
   * draws it — as the AI notice module the consumer puts on a marked card.
   * Apple carries the mark in `userInfo`, which no device shows. Only the
   * builder passes it; every other caller draws exactly what it drew.
   */
  aiDisclosureMode?: GoogleAiDisclosureMode | null;
};

export function WalletPreview({
  config,
  currentLanguage,
  platform = "apple",
  event,
  eventId,
  primaryLanguage,
  preSubstituted = false,
  deviceOnly = false,
  rows,
  aiDisclosureMode = null,
}: Props) {
  // Installed HERE rather than at each caller: this component has five JSX call
  // sites (editor — mounted by both the event and the template builder page —
  // send dialog, create wizard, preset gallery, preset card) and only two of
  // them have an event to give. The provider
  // always exists, so no preview bit has to cope with its absence.
  const base = useMemo(
    () => buildWalletSamples(event, currentLanguage, eventId, primaryLanguage),
    [event, currentLanguage, eventId, primaryLanguage],
  );

  // The renderer's drop rule, as far as it can be decided WITHOUT a guest.
  // Installed here rather than at the callers because all six of them drew the
  // same lie, and only two of them ever apply the fuller guest-lane rule on top.
  //
  // The two lanes that DO apply the guest rule hand in a config that has already
  // been through it, and they enrich the guest with the event variables first
  // (`AttachmentPreview` → `enrichWithEventVars`), so every event token in their
  // config is resolved before it arrives. For them this pass has nothing left to
  // decide and `preSubstituted` turns it off.
  //
  // It is off because it is WRONG there, not merely redundant. Two earlier
  // versions of this comment argued it was safe; both were disproved:
  //
  //  1. "a substituted config has no tokens left to decide" — an unresolvable
  //     guest token substitutes to "" rather than staying a token, so the second
  //     pass saw an emptiness it had not caused and deleted rows the guest lane
  //     had deliberately protected. The `nothingConfigured` exemption fixed that
  //     one case.
  //  2. "`applyEventTokenRules` is IDEMPOTENT" — it is not, and the exemption
  //     does not make it so. `TemplateVarSubstitutor.substitute` is SINGLE-PASS
  //     over the original text, so a `{{eventLocation}}` that arrives inside
  //     GUEST DATA (a CSV import, a free-text field) is never substituted by the
  //     renderer and ships on the pass as literal text. A second pass here
  //     resolves it, finds the event has no venue, and deletes the row. Preview
  //     blank, pass with a literal token — and the caption that would have named
  //     it is suppressed in the guest lane.
  //
  // Two failed arguments for the same claim is the signal to stop arguing and
  // remove the composition instead.
  const parity = useMemo(
    () =>
      preSubstituted
        ? { config, droppedForEvent: [], uncertain: {} }
        : applyEventTokenRules(config, {
            lang: currentLanguage,
            primaryLang: primaryLanguage || currentLanguage,
            eventValues: base.eventValues,
            eventLoaded: base.eventLoaded,
            // The Google lane's substituter trims; Apple's does not. Without
            // this the rule deleted a row from the GOOGLE preview that the
            // Google card resolves perfectly well, and named it as one no guest
            // will receive.
            applePaddingRules: platform === "apple",
          }),
    [
      config,
      preSubstituted,
      platform,
      currentLanguage,
      primaryLanguage,
      base.eventValues,
      base.eventLoaded,
    ],
  );

  return (
    <WalletSamplesProvider samples={base}>
      <DeviceOnlyProvider deviceOnly={deviceOnly}>
        <PassCertaintyProvider rows={rows}>
          <PassBody
            config={parity.config}
            currentLanguage={currentLanguage}
            platform={platform}
            aiDisclosureMode={aiDisclosureMode}
          />
        </PassCertaintyProvider>
      </DeviceOnlyProvider>
    </WalletSamplesProvider>
  );
}

/**
 * What the preview could and could not decide, for a caller that wants to say
 * so in words.
 *
 * Same inputs, same function, so a caption built from this cannot disagree with
 * the card beside it. Callers that only draw a pass (the gallery card, the
 * create wizard) ignore it and lose nothing — the card is already honest.
 *
 * <p>A caller that uses BOTH this and `WalletPreview` runs `buildWalletSamples`
 * and the rule twice per dependency change. Left as it is deliberately: both are
 * pure and memoized on the same inputs, so it is duplicate work rather than
 * re-render churn, and the alternatives — threading the result in as a prop, or
 * caching the samples at module scope — add an interface that can be wired
 * wrongly to save a computation nobody has measured as a cost.
 */
export function useWalletPassParity(
  config: WalletConfig,
  currentLanguage: string,
  event?: Event | null,
  eventId?: string,
  primaryLanguage?: string,
  /**
   * Which platform's card the caption is sitting UNDER.
   *
   * Not cosmetic, and not optional in spirit: `applePaddingRules` decides
   * whether a padded token (`{{ firstName }}`) counts as dropped or as merely
   * uncertain, because Apple's substituter looks the name up untrimmed while
   * Google's trims and resolves it. This hook used to omit the flag entirely
   * and take its `true` default, so on the Google tab the card KEPT such a row
   * while the caption underneath announced it had been removed — and the "may
   * not reach this guest" caption went silent for the same row, sending the
   * operator off to fix a field that works.
   *
   * Defaults to `"apple"` so the pre-existing behaviour is what an un-migrated
   * caller still gets, rather than a silent flip in the other direction.
   */
  platform: WalletPlatform = "apple",
): EventTokenParity {
  const base = useMemo(
    () => buildWalletSamples(event, currentLanguage, eventId, primaryLanguage),
    [event, currentLanguage, eventId, primaryLanguage],
  );
  return useMemo(
    () =>
      applyEventTokenRules(config, {
        lang: currentLanguage,
        primaryLang: primaryLanguage || currentLanguage,
        eventValues: base.eventValues,
        eventLoaded: base.eventLoaded,
        // Same argument the card passes at the call site above. That identity
        // is the hook's whole promise — a caption built from this cannot
        // disagree with the card beside it.
        applePaddingRules: platform === "apple",
      }),
    [
      config,
      currentLanguage,
      primaryLanguage,
      platform,
      base.eventValues,
      base.eventLoaded,
    ],
  );
}

function PassBody({
  config,
  currentLanguage,
  platform,
  aiDisclosureMode,
}: {
  config: WalletConfig;
  currentLanguage: string;
  platform: WalletPlatform;
  aiDisclosureMode: GoogleAiDisclosureMode | null;
}) {
  // Google's GenericObject is one fixed card for every passType, so the platform
  // switch comes first; Apple keeps its passType-specific layouts below.
  if (platform === "google") {
    return (
      <GooglePassPreview
        config={config}
        currentLanguage={currentLanguage}
        aiDisclosureMode={aiDisclosureMode}
      />
    );
  }
  switch (getPassType(config)) {
    case "boardingPass":
      return (
        <BoardingPassPreview
          config={config}
          currentLanguage={currentLanguage}
        />
      );
    case "coupon":
      return <CouponPreview config={config} currentLanguage={currentLanguage} />;
    case "generic":
      return <GenericPreview config={config} currentLanguage={currentLanguage} />;
    case "storeCard":
      return (
        <StoreCardPreview config={config} currentLanguage={currentLanguage} />
      );
    case "eventTicket":
    default:
      return (
        <EventTicketPreview config={config} currentLanguage={currentLanguage} />
      );
  }
}
